import {
  isGreenhouseIntegrationSettings,
  type GreenhouseIntegrationSettings,
} from "@repo/app-types/entities/integration";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { SaveAndCancelButtons } from "@repo/ui/Buttons/saveAndCancelButtons";
import { capitalize, Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Label } from "@repo/ui/Label/label";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { IconCircleCheck } from "@tabler/icons-react";
import { useEffect, useState, type FC } from "react";
import { getAxiosInstanceWithAuth } from "../../../api/axiosConfig.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import {
  deleteIntegration,
  selectGreenhouseIntegration,
  updateIntegration,
  upsertIntegration,
} from "../../integrations/integrationsSlice.js";

export const GreenhouseIntegrationSection: FC = () => {
  const dispatch = useAppDispatch();
  const greenhouseIntegration = useAppSelector(selectGreenhouseIntegration);

  const [apiToken, setApiToken] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isRemoveAccountModalOpen, setIsRemoveAccountModalOpen] =
    useState(false);
  const [advancementStageName, setAdvancementStageName] = useState("");
  const [integrationSystemUserEmail, setIntegrationSystemUserEmail] =
    useState("");
  const [isUpdatingSettings, setIsUpdatingSettings] = useState(false);

  const savedAdvancementStageName =
    (greenhouseIntegration?.settings as GreenhouseIntegrationSettings)
      ?.advancement_stage_name ?? "";

  const savedIntegrationSystemUserEmail =
    (greenhouseIntegration?.settings as GreenhouseIntegrationSettings)
      ?.integration_system_user_email ?? "";

  const hasEditedAdvancementStage =
    advancementStageName.trim() !== savedAdvancementStageName.trim();

  const hasEditedIntegrationSystemUserEmail =
    integrationSystemUserEmail.trim() !==
    savedIntegrationSystemUserEmail.trim();

  // Set the current settings when the integration loads
  useEffect(() => {
    if (
      greenhouseIntegration?.settings &&
      isGreenhouseIntegrationSettings(greenhouseIntegration.settings)
    ) {
      setAdvancementStageName(
        greenhouseIntegration.settings.advancement_stage_name || "",
      );
      setIntegrationSystemUserEmail(
        greenhouseIntegration.settings.integration_system_user_email || "",
      );
    }
  }, [greenhouseIntegration]);

  const handleSaveIntegration = async () => {
    if (!apiToken.trim()) {
      showToast({
        title: "API token is required",
        variant: ToastVariant.Error,
      });
      return;
    }

    setIsLoading(true);
    try {
      const axios = await getAxiosInstanceWithAuth();

      if (greenhouseIntegration) {
        // Update existing integration - just update the access token
        const { data } = await axios.patch(
          `/integrations/${greenhouseIntegration.id}`,
          {
            access_token: apiToken,
          },
        );
        dispatch(upsertIntegration(data));
      } else {
        // Create new integration via API
        const { data } = await axios.post("/integrations/greenhouse", {
          access_token: apiToken,
        });
        dispatch(upsertIntegration(data));
      }

      showToast({
        title: "Greenhouse integration saved successfully",
        variant: ToastVariant.Success,
      });

      setApiToken(""); // Clear the input after successful save
    } catch (error) {
      showToast({
        title: "Failed to save Greenhouse integration",
        variant: ToastVariant.Error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setApiToken(""); // Clear the input when canceling
  };

  const handleUpdateSettings = async () => {
    if (!greenhouseIntegration) return;

    setIsUpdatingSettings(true);
    try {
      dispatch(
        updateIntegration({
          id: greenhouseIntegration.id,
          changes: {
            settings: {
              ...(greenhouseIntegration.settings &&
              isGreenhouseIntegrationSettings(greenhouseIntegration.settings)
                ? greenhouseIntegration.settings
                : {}),
              advancement_stage_name: advancementStageName.trim() || null,
              integration_system_user_email:
                integrationSystemUserEmail.trim() || null,
            },
          },
        }),
      );

      showToast({
        title: "Greenhouse settings updated successfully",
        variant: ToastVariant.Success,
      });
    } catch (error) {
      showToast({
        title: "Failed to update Greenhouse settings",
        variant: ToastVariant.Error,
      });
    } finally {
      setIsUpdatingSettings(false);
    }
  };

  const handleCancelSettings = () => {
    setAdvancementStageName(savedAdvancementStageName);
    setIntegrationSystemUserEmail(savedIntegrationSystemUserEmail);
  };

  const renderContent = () => {
    if (!greenhouseIntegration) {
      return (
        <Can
          I={PermissionActions.CREATE}
          a={PermissionSubjects.INTEGRATIONS}
          passThrough={true}
        >
          {(allowed) => (
            <div className="flex flex-col space-y-4">
              <div className="w-96">
                <Input
                  label="Greenhouse API token"
                  placeholder="Enter your Greenhouse API token"
                  value={apiToken}
                  onChange={(e) => setApiToken(e.target.value)}
                  isDisabled={!allowed || isLoading}
                  maxLength={100}
                />
              </div>
              {apiToken.trim() && (
                <SaveAndCancelButtons
                  onSave={handleSaveIntegration}
                  onCancel={handleCancel}
                  isSaving={isLoading}
                  isSaveDisabled={!apiToken.trim()}
                />
              )}
            </div>
          )}
        </Can>
      );
    }

    // Integration exists - show connected state with update options
    return (
      <div className="flex flex-col space-y-5">
        <div>
          <Label size={Size.SMALL}>Account</Label>
          <div className="flex flex-row items-center space-x-3 text-sm">
            <div className="flex flex-row items-center space-x-1">
              <IconCircleCheck className="h-5 w-5 text-green-500" />
              <div>
                {capitalize(greenhouseIntegration.provider)} API token
                successfully saved
              </div>
            </div>
            <Can
              I={PermissionActions.DELETE}
              a={PermissionSubjects.INTEGRATIONS}
              passThrough={true}
            >
              {(deleteAllowed) => (
                <Button
                  onClick={() => setIsRemoveAccountModalOpen(true)}
                  size={Size.SMALL}
                  variant={ButtonVariant.Warning}
                  isDisabled={!deleteAllowed || isLoading}
                >
                  Remove
                </Button>
              )}
            </Can>
          </div>
        </div>

        <Can
          I={PermissionActions.UPDATE}
          a={PermissionSubjects.INTEGRATIONS}
          passThrough={true}
        >
          {(allowed) => (
            <div className="space-y-4">
              <div>
                <div className="w-48">
                  <Input
                    label="Advancement stage name"
                    inputClassName="w-48"
                    placeholder="Final round interview"
                    value={advancementStageName}
                    onChange={(e) => setAdvancementStageName(e.target.value)}
                    isDisabled={!allowed || isUpdatingSettings}
                    maxLength={100}
                  />
                </div>
                <div className="mt-2 text-sm text-muted-foreground">
                  The name of the Greenhouse stage to move candidates to once
                  they are advanced in Alpharun.
                </div>
              </div>

              <div>
                <div className="w-52">
                  <Input
                    label="Integration system user email"
                    inputClassName="w-52"
                    placeholder="name@company.com"
                    value={integrationSystemUserEmail}
                    onChange={(e) =>
                      setIntegrationSystemUserEmail(e.target.value)
                    }
                    isDisabled={!allowed || isUpdatingSettings}
                    maxLength={100}
                  />
                </div>
                <div className="mt-2 text-sm text-muted-foreground">
                  The email address of the Greenhouse integration system user to
                  perform actions on behalf of.
                </div>
              </div>

              {(hasEditedAdvancementStage ||
                hasEditedIntegrationSystemUserEmail) && (
                <SaveAndCancelButtons
                  onSave={handleUpdateSettings}
                  onCancel={handleCancelSettings}
                  isSaving={isUpdatingSettings}
                />
              )}
            </div>
          )}
        </Can>

        <SimpleModal
          isOpen={isRemoveAccountModalOpen}
          onCancel={() => setIsRemoveAccountModalOpen(false)}
          variant={SimpleModalVariant.Warning}
          title="Are you sure?"
          subtitle="Removing your Greenhouse account will stop all data from syncing."
          confirmButtonText="Remove account"
          onConfirm={() => {
            dispatch(deleteIntegration(greenhouseIntegration.id));
            setIsRemoveAccountModalOpen(false);
          }}
        />
      </div>
    );
  };

  return (
    <div>
      <div className="mb-4">
        <Label size={Size.MEDIUM}>Greenhouse</Label>
      </div>
      {renderContent()}
    </div>
  );
};
